import {
  ALL_FIM_ITEMS,
  type AssessmentRegistrationFormInput,
  AssessmentRegistrationFormSchema,
  DateSchema,
  ERROR_CODE,
  FIM_DOMAIN,
  FIM_ITEM,
  INDEPENDENCE_LEVEL,
  INDEPENDENCE_LEVEL_TYPE,
  UniversalError,
  getFimItemSchema,
  parse,
  parseIndependenceLevels,
} from "@packages/core";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate, useRouter } from "@tanstack/react-router";
import { Fragment, useId, useMemo } from "react";
import { Temporal } from "temporal-polyfill";
import * as v from "valibot";

import {
  Breadcrumbs,
  ButtonStyle,
  FormField,
  IndependenceLevelSelector,
  PaperStyle,
  TypographyStyle,
  showError,
} from "#components";
import {
  $alert,
  $confirm,
  findBeds,
  fromFimDomainToColor,
  registerIndependenceLevels,
  useGetErrorMessage,
  useLatestIndependenceLevelEntryQuery,
  useResident,
  useTranslate,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/residents/$userKey/independence-levels/registration",
)({
  component: Component,
  validateSearch: v.object({
    date: v.optional(v.fallback(v.optional(DateSchema()), undefined)),
    fimItem: v.optional(
      v.fallback(
        v.pipe(
          v.union([
            v.pipe(
              v.string(),
              v.transform(function (input) {
                return Number.parseInt(input);
              }),
            ),
            v.number(),
          ]),
          getFimItemSchema(),
        ),
        FIM_ITEM.EATING,
      ),
      FIM_ITEM.EATING,
    ),
  }),
});

function Component() {
  const { facilityKey, userKey } = Route.useParams();
  const { date, fimItem } = Route.useSearch();

  const getErrorMessage = useGetErrorMessage();
  const navigate = useNavigate();
  const router = useRouter();
  const translate = useTranslate();

  const resident = useResident(userKey);

  const latestIndependenceLevelEntryQuery = useLatestIndependenceLevelEntryQuery(userKey);

  const formId = useId();

  const schema = AssessmentRegistrationFormSchema();
  const defaultValues = useMemo(
    function (): Partial<AssessmentRegistrationFormInput> {
      return {
        assessedAt: Temporal.Now.plainDateTimeISO("Asia/Tokyo").with({
          second: 0,
          millisecond: 0,
          microsecond: 0,
          nanosecond: 0,
        }),
        ...Object.fromEntries(
          ALL_FIM_ITEMS.map(function (fimItem) {
            return [fimItem, INDEPENDENCE_LEVEL.COMPLETE_INDEPENDENCE];
          }),
        ),
        ...(latestIndependenceLevelEntryQuery.data &&
          Object.fromEntries(
            latestIndependenceLevelEntryQuery.data.independenceLevels.map(
              function (independenceLevel, index) {
                return [index, independenceLevel];
              },
            ),
          )),
      };
    },
    [latestIndependenceLevelEntryQuery.data],
  );

  const { Field, Subscribe, handleSubmit, store } = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      try {
        const output = parse(schema, input as any);
        console.info("[onSubmit]", new Date(), { input, output });

        const { beds, errors } = await findBeds({
          index: "userKey-gsi",
          condition: [["userKey", "=", userKey]],
          limit: 1,
        });

        if (errors.length > 0) {
          for (const error of errors) {
            console.error("ベッドの情報の取得に失敗しました。", error);
          }
          throw errors.at(0);
        }

        const bed = beds.at(0);

        if (!bed) {
          throw new UniversalError(ERROR_CODE.RESIDENT_BED_NOT_FOUND);
        }

        const assessment = await registerIndependenceLevels({
          independenceLevels: parseIndependenceLevels(output),
          type: INDEPENDENCE_LEVEL_TYPE.OBSERVED,
          userKey,
        });
        console.info("評価データを登録しました。", { assessment });

        await $alert({
          color: "success",
          content: translate("%Settings.1%"),
          title: translate("%Complete.0%"),
        });

        await navigate({
          ignoreBlocker: true,
          params: { facilityKey, userKey },
          to: "/facilities/$facilityKey/residents/$userKey",
        });
      } catch (error) {
        showError(error);
      }
    },
    async onSubmitInvalid({ value: input }) {
      console.info("[onSubmitInvalid]", new Date(), { input });
      showError(new UniversalError(ERROR_CODE.INVALID_FORM));
    },
    validators: {
      onSubmit: schema,
    },
  });

  {
    const isDefaultValue = useStore(store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(store, (state) => state.isSubmitted);
    const isSubmitting = useStore(store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: translate("%Form.1%"),
              no: translate("%Stay.0%"),
              title: translate("%Form.0%"),
              yes: translate("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  return (
    <main>
      <div
        className={css({
          alignItems: "center",
          display: "flex",
          gap: "8px",
          justifyContent: "space-between",
        })}
      >
        <div className={css({ hideFrom: "sm" })} />
        <Breadcrumbs
          className={css(
            TypographyStyle.raw({ fontSize: "14px", fontWeight: 500, lineHeight: 1 }),
            { display: "flex", hideBelow: "sm" },
          )}
        >
          <Link
            className={css({ color: "#71717A" })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey"
          >
            {translate("%ResidentList.0%")}
          </Link>
          <Link
            className={css({ color: "#71717A" })}
            params={{ facilityKey, userKey }}
            to="/facilities/$facilityKey/residents/$userKey"
          >
            {translate("%HonorificTitle.0%", {
              $: resident ?? { name: translate("%Resident.0%") },
            })}
          </Link>
          <Link
            className={css({ color: "#71717A" })}
            params={{ facilityKey, fimItem, userKey }}
            search={{ date }}
            to="/facilities/$facilityKey/residents/$userKey/independence-levels/$fimItem"
          >
            {translate("%FimItem." + fimItem + ".0%")}
          </Link>
          <p>{translate("%Edit.0%")}</p>
        </Breadcrumbs>

        <div className={css({ display: "flex", gap: "8px", justifyContent: "space-between" })}>
          <Link
            className={ButtonStyle({
              appearance: "ghost",
              borderRadius: "4px",
              color: "secondary",
              fontSize: "14px",
              padding: "10px 16px",
            })}
            onClick={function (event) {
              if (router.history.canGoBack()) {
                event.preventDefault();
                router.history.back();
              }
            }}
            params={{ facilityKey, userKey }}
            to="/facilities/$facilityKey/residents/$userKey"
          >
            {translate("%Cancel.0%")}
          </Link>
          <Subscribe
            selector={function ({ canSubmit }) {
              return canSubmit;
            }}
          >
            {function (canSubmit) {
              return (
                <button
                  className={ButtonStyle({
                    appearance: "contained",
                    borderRadius: "4px",
                    color: "primary",
                    fontSize: "14px",
                    padding: "10px 16px",
                  })}
                  disabled={!canSubmit}
                  form={formId}
                  type="submit"
                >
                  {translate("%Save.0%")}
                </button>
              );
            }}
          </Subscribe>
        </div>
      </div>

      <form
        className={css(PaperStyle.raw({ borderRadius: "12px", marginTop: "12px" }), {
          padding: { base: "16px", sm: "24px" },
        })}
        id={formId}
        onSubmit={function (event) {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <Field name="assessedAt">
          {function (field) {
            return (
              <input
                className={TypographyStyle({
                  display: "block",
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: 1,
                  textAlign: "center",
                  width: "stretch",
                })}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  try {
                    field.setValue(
                      event.target.value
                        ? Temporal.PlainDateTime.from(event.target.value)
                        : undefined,
                    );
                  } catch {
                    field.setValue(undefined);
                  }
                }}
                type="datetime-local"
                value={field.state.value ? field.state.value.toString() : ""}
              />
            );
          }}
        </Field>

        {(
          [
            [
              FIM_DOMAIN.SELF_CARE,
              [
                FIM_ITEM.EATING,
                FIM_ITEM.GROOMING,
                FIM_ITEM.BATHING,
                FIM_ITEM.UPPER_BODY_DRESSING,
                FIM_ITEM.LOWER_BODY_DRESSING,
                FIM_ITEM.TOILETING,
              ],
            ],
            [
              FIM_DOMAIN.SPHINCTER_CONTROL,
              [FIM_ITEM.BLADDER_MANAGEMENT, FIM_ITEM.BOWEL_MANAGEMENT],
            ],
            [
              FIM_DOMAIN.TRANSFER,
              [FIM_ITEM.BED_CHAIR_WHEELCHAIR, FIM_ITEM.TOILET, FIM_ITEM.TUB_SHOWER],
            ],
            [FIM_DOMAIN.LOCOMOTION, [FIM_ITEM.WALK_WHEELCHAIR, FIM_ITEM.STAIRS]],
            [FIM_DOMAIN.COMMUNICATION, [FIM_ITEM.COMPREHENSION, FIM_ITEM.EXPRESSION]],
            [
              FIM_DOMAIN.SOCIAL_COGNITION,
              [FIM_ITEM.SOCIAL_INTERACTION, FIM_ITEM.PROBLEM_SOLVING, FIM_ITEM.MEMORY],
            ],
          ] as const
        ).map(function ([fimDomain, fimItems]) {
          return (
            <Fragment key={fimDomain}>
              <h2
                className={TypographyStyle({
                  ellipsis: true,
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: 1,
                  marginTop: "16px",
                })}
                style={{ color: fromFimDomainToColor(fimDomain) }}
              >
                {translate("%FimDomain." + fimDomain + ".0%")}
              </h2>

              <div
                className={css({
                  display: "grid",
                  columnGap: "8px",
                  gridTemplateColumns: {
                    base: "auto",
                    sm: "repeat(3, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                    lg: "repeat(5, minmax(0, 1fr))",
                    xl: "repeat(6, minmax(0, 1fr))",
                  },
                  rowGap: "12px",
                  marginTop: "12px",
                })}
              >
                {fimItems.map(function (fimItem) {
                  return (
                    <Field
                      key={fimItem}
                      name={`${fimItem}`}
                      validators={{ onChange: schema.entries[fimItem] }}
                    >
                      {function (field) {
                        return (
                          <FormField
                            caution={field.state.meta.errors
                              .map(function (error) {
                                return getErrorMessage(error);
                              })
                              .join("")}
                            invalid={field.state.meta.errors.length > 0}
                            key={fimItem}
                            label={translate("%FimItem." + fimItem + ".0%")}
                          >
                            <IndependenceLevelSelector
                              className={css({ width: "stretch" })}
                              fimItem={fimItem}
                              loading={latestIndependenceLevelEntryQuery.isLoading}
                              onChange={function (value) {
                                field.setValue(value);
                              }}
                              value={field.state.value}
                            />
                          </FormField>
                        );
                      }}
                    </Field>
                  );
                })}
              </div>
            </Fragment>
          );
        })}
      </form>
      <div className={css({ height: "16px" })} />
    </main>
  );
}
